import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllCollectionCash,
  deleteCollectionCash,
} from "../../api/collectionCashApi";
import { toast } from "react-toastify";
import SafeImage from "../../components/common/SafeImage";

/**
 * CollectionCash Component
 * Dashboard for managing driver collection deposits (bank deposits from drivers)
 * Provides CRUD operations for collection records with local storage
 */
const CollectionCash = () => {
  const navigate = useNavigate();
  const [deposits, setDeposits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [depositToDeleteId, setDepositToDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  // Load collection deposits from local storage on component mount
  useEffect(() => {
    const loadDeposits = async () => {
      try {
        const data = await getAllCollectionCash();
        setDeposits(data);
      } catch (error) {
        toast.error("Failed to load collection cash");
      }
    };
    loadDeposits();
  }, []);

  // Handle delete confirmation modal display
  const handleDeleteClick = (id) => {
    setDepositToDeleteId(id);
    setShowModal(true);
  };

  // Execute collection deposit deletion and update local storage
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await deleteCollectionCash(depositToDeleteId);
      setDeposits(
        deposits.filter((deposit) => deposit.id !== depositToDeleteId),
      );
      toast.success("Deposit deleted successfully");
    } catch (error) {
      toast.error("Failed to delete deposit");
    } finally {
      setShowModal(false);
      setDepositToDeleteId(null);
      setDeleteLoading(false);
    }
  };

  // Cancel delete operation and close modal
  const handleDeleteCancel = () => {
    setShowModal(false);
    setDepositToDeleteId(null);
  };

  // Handle receipt details view
  const handleReceiptClick = (deposit) => {
    setSelectedDeposit(deposit);
    setShowImageModal(true);
  };

  // Close receipt modal
  const handleReceiptModalClose = () => {
    setShowImageModal(false);
    setSelectedDeposit(null);
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return (
      `${import.meta.env.VITE_API_URL}${imagePath}` ||
      `http://localhost:8000${imagePath}`
    );
  };

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5>Collection Cash</h5>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/accounts/add-collection-cash")}
            >
              Add Collection
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped theme-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Driver Name</th>
                  <th>Deposit Amount</th>
                  <th>Bank Name</th>
                  <th>Receipt Image</th>
                  <th>Deposit Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deposits.length > 0 ? (
                  deposits.map((deposit) => (
                    <tr key={deposit.id}>
                      <td>{deposit.id}</td>
                      <td>{deposit.driver_name}</td>
                      <td>${deposit.deposit_amount}</td>
                      <td>{deposit.bank_name}</td>
                      <td>
                        {deposit.receipt_image ? (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleReceiptClick(deposit)}
                            style={{ fontSize: "12px" }}
                          >
                            View Receipt
                          </button>
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </td>
                      <td>
                        {new Date(deposit.deposit_date).toLocaleDateString()}
                      </td>
                      <td>
                        <ul className="d-flex gap-2 justify-content-center">
                          <li>
                            <button
                              className="btn btn-primary btn-sm"
                              style={{
                                width: "60px",
                                height: "30px",
                                fontSize: "12px",
                                border: "2px solid rgb(0, 195, 133)",
                              }}
                              onClick={() =>
                                navigate(
                                  `/accounts/edit-collection-cash/${deposit.id}`,
                                )
                              }
                            >
                              Edit
                            </button>
                          </li>
                          <li>
                            <button
                              className="btn btn-danger btn-sm"
                              style={{
                                width: "60px",
                                height: "30px",
                                fontSize: "12px",
                                border: "2px solid #dc3545",
                              }}
                              onClick={() => handleDeleteClick(deposit.id)}
                            >
                              Delete
                            </button>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No deposits found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div
        className={`modal fade ${showModal ? "show" : ""}`}
        style={{ display: showModal ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleDeleteCancel}
              ></button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete this deposit? This action cannot
                be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                ) : null}
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="modal-backdrop fade show"></div>}

      {/* Receipt Details Modal */}
      <div
        className={`modal fade ${showImageModal ? "show" : ""}`}
        style={{ display: showImageModal ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Receipt Details</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleReceiptModalClose}
              ></button>
            </div>
            <div className="modal-body">
              {selectedDeposit && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <strong>Driver Name:</strong>
                      <p className="mb-2">{selectedDeposit.driver_name}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Deposit Amount:</strong>
                      <p className="mb-2">${selectedDeposit.deposit_amount}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Bank Name:</strong>
                      <p className="mb-2">{selectedDeposit.bank_name}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Receipt Number:</strong>
                      <p className="mb-2">
                        {selectedDeposit.receipt_number || "Not provided"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <strong>Deposit Date:</strong>
                      <p className="mb-2">
                        {new Date(
                          selectedDeposit.deposit_date,
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    {selectedDeposit.notes && (
                      <div className="mb-3">
                        <strong>Notes:</strong>
                        <p className="mb-2">{selectedDeposit.notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <div className="text-center">
                      <strong>Receipt Image:</strong>
                      {selectedDeposit.receipt_image ? (
                        <div className="mt-2">
                          <SafeImage
                            src={getImageUrl(selectedDeposit.receipt_image)}
                            alt="Receipt"
                            className="img-fluid border rounded"
                            style={{ maxHeight: "400px", maxWidth: "100%" }}
                          />
                        </div>
                      ) : (
                        <div className="mt-2 p-4 border rounded bg-light">
                          <p className="text-muted mb-0">
                            No receipt image available
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReceiptModalClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      {showImageModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default CollectionCash;
